import type { PopperPlacementType } from '@mui/material';
import type { ReactNode } from 'react';

export type AnchoredPopupPlacement =
  | 'bottom-start'
  | 'bottom-end'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'top'
  | 'right-start'
  | 'right-end'
  | 'right'
  | 'left-start'
  | 'left-end'
  | 'left';

export interface AnchoredPopupProps {
  /** The anchor element that the popup is positioned relative to */
  anchorEl: HTMLElement | null;

  /** Whether the popup is open */
  open: boolean;

  /** Callback when the popup should close */
  onClose: () => void;

  /** The content to render inside the popup */
  children: ReactNode;

  /** Optional ID for the popup element */
  id?: string;

  /**
   * Preferred placement of the popup relative to anchor.
   * - 'right-start': Right side, aligned to top (default)
   * - 'left-start': Left side, aligned to top (fallback when no space on right)
   * Default: 'right-start'
   */
  placement?: AnchoredPopupPlacement;

  /** Width of the popup. Can be a number (px) or CSS string. Default: 'auto' */
  width?: number | string;

  /** Maximum height of the popup. Default: 'none' */
  maxHeight?: number | string;

  /** Whether to disable the portal. Default: false */
  disablePortal?: boolean;

  /** Additional aria-label for accessibility */
  ariaLabel?: string;

  /**
   * z-index for the popup.
   * If useStackContext is true (default), this is used as fallback only.
   * If useStackContext is false, this value is used directly.
   * Default: 1300
   */
  zIndex?: number;

  /**
   * Whether to use the PopupStackContext for z-index management.
   * When true, the popup registers with the global stack and gets an
   * automatically assigned z-index range.
   * Default: true
   */
  useStackContext?: boolean;

  /**
   * Callback to receive child z-index getter function.
   * Called when the popup registers with the stack context.
   * Use this to pass z-index information to child components.
   */
  onStackRegistered?: (getChildZIndex: (position: 'behind' | 'front') => number) => void;

  /** Gap between anchor and popup in pixels. Default: 8 */
  offset?: number;

  /** Whether to show an animation. Default: true */
  animated?: boolean;

  /** Callback fired when the open animation completes */
  onOpenComplete?: () => void;

  /**
   * Additional width to reserve on the end side (e.g., for a sub-panel).
   * This adjusts the preventOverflow modifier to ensure space for content
   * that extends beyond the popup's own width.
   */
  reserveEndWidth?: number;
}

export interface PopperModifier {
  name: string;
  enabled?: boolean;
  phase?:
    | 'read'
    | 'main'
    | 'afterRead'
    | 'beforeMain'
    | 'afterMain'
    | 'beforeWrite'
    | 'write'
    | 'afterWrite';
  options?: Record<string, unknown>;
  fn?: (arg: { state: { placement: string } }) => void;
}

/** Maps AnchoredPopupPlacement to MUI PopperPlacementType */
export function toPopperPlacement(placement: AnchoredPopupPlacement): PopperPlacementType {
  return placement as PopperPlacementType;
}
